import {ShopBranch, UserModel} from '../auth/core/_models'

/**
 * Mart v2: a supplier may run a partner shop per BRANCH, and the portal API answers every shop
 * question — the shelf, the branch's price, its discounts — for the branch named in the
 * `branch-id` header. A user tied to a branch always works for it (the server ignores the header);
 * an all-branches user picks one in the header bar. The pick is kept per browser, so a reload stays
 * on the same branch, and is cleared on sign-in and sign-out.
 */
export const BRANCH_HEADER = 'branch-id'

const STORAGE_KEY = 'hisbah.acting_branch_id'

export const getActingBranch = (): number | null => {
  try {
    const id = Number(localStorage.getItem(STORAGE_KEY))
    return Number.isInteger(id) && id > 0 ? id : null
  } catch {
    return null
  }
}

export const setActingBranch = (branchId: number | null): void => {
  try {
    if (branchId) {
      localStorage.setItem(STORAGE_KEY, String(branchId))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    /* storage blocked: the server falls back to the main branch */
  }
}

/** The supplier's shops, main branch first. Empty for an ordinary supplier. */
export const branchesOf = (user?: UserModel | null): ShopBranch[] => user?.shops ?? []

/** May this user choose the branch they work for? Only an all-branches user of a multi-shop supplier. */
export const canChooseBranch = (user?: UserModel | null): boolean =>
  !!user && !user.supplier_branch_id && branchesOf(user).length > 1

/**
 * The branch to send for this user: the stored pick while it is still one of their branches, else
 * the main branch. Null when there is nothing to choose — the server then decides on its own.
 */
export const resolveActingBranch = (user: UserModel, stored: number | null): number | null => {
  if (!canChooseBranch(user)) {
    return null
  }
  const branches = branchesOf(user)
  return branches.some((b) => b.branch_id === stored) ? stored : branches[0].branch_id
}

/** The shop the user is working at now, or null. */
export const actingShopBranch = (user?: UserModel | null): ShopBranch | null => {
  const branches = branchesOf(user)
  const id = user?.supplier_branch_id || user?.acting_branch_id
  return branches.find((b) => b.branch_id === id) ?? null
}

/** Did the server refuse the branch we sent (deactivated, or not this supplier's)? */
export const isBranchRefusal = (err: any): boolean =>
  err?.response?.status === 403 && err?.response?.data?.status_code === 'branch_not_found'

/**
 * Query params that ask for an edit on every shop branch instead of the acting one. The server reads
 * `apply_to_all_branches` from the query string as well as the body, so one helper serves JSON and
 * multipart (sheet) requests alike.
 */
export const branchScopeParams = (allBranches?: boolean): Record<string, number> =>
  allBranches ? {apply_to_all_branches: 1} : {}
